import { Select, type ComboboxItem } from '@cowbell/ui/core';
import { faCommentDots } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './ToneSelect.module.css';

export interface ToneSelectProps {
  readonly tones: Array<{ readonly id: string; readonly name: string }>;
  /** Currently selected tone id, or null/undefined for none. */
  readonly tone?: string | null;
  /** Called when the user picks or clears a tone. Mantine Select passes
   *  (value, option) — the second arg is the selected ComboboxItem or null. */
  readonly onChange?: (
    tone: string | null,
    option: ComboboxItem | null
  ) => void;
  readonly disabled?: boolean;
}

/**
 * The response-tone selector, shown in the Sources popover just below
 * "Web search" — moved off the composer's action row, where it took
 * prime space for a rarely-used control. Renders nothing when the host has
 * no tones configured.
 */
export function ToneSelect({
  tones,
  tone,
  onChange,
  disabled,
}: ToneSelectProps) {
  if (tones.length === 0) return null;

  return (
    <div className={styles.row}>
      <span className={styles.rowIcon}>
        <FontAwesomeIcon icon={faCommentDots} />
      </span>
      <span className={styles.label}>Tone</span>
      <Select
        aria-label="Response tone"
        placeholder="Default"
        size="xs"
        clearable
        classNames={{ input: styles.input }}
        data={tones.map((t) => ({ value: t.id, label: t.name }))}
        value={tone ?? null}
        onChange={onChange}
        disabled={disabled}
        // withinPortal: false keeps the option list a DOM descendant of the
        // outer Sources popover instead of a sibling in the shared portal
        // root. Two things depend on that: the popover's own outside-click
        // handling sees clicking an option as "inside" (portaled-true made
        // picking a tone dismiss the whole popover), and the options then
        // simply inherit the outer popover's zIndex stacking instead of
        // needing one of their own.
        comboboxProps={{ withinPortal: false }}
        w={140}
      />
    </div>
  );
}
